import { useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client, CombinedError } from "urql"
import { useShallow } from "zustand/react/shallow"

import { PENDING_APPROVALS_ROUTE } from "../constants"
import type { QualityApprovalSearchFilters } from "../stores/useQualityApprovalSearchStore"
import { useQualityApprovalSearchStore } from "../stores/useQualityApprovalSearchStore"

import type { QualityApproval } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseQualityApprovalListReturn {
  qualityApprovals: QualityApproval[]
  isFetching: boolean
  error: CombinedError | Error | undefined
  sorting: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
}

const ROUTE = "/app/dashboard/_layout/pending-approvals/_layout/quality"

export const useQualityApprovalList = (): UseQualityApprovalListReturn => {
  const { taskViewScope } = useSearch({ from: PENDING_APPROVALS_ROUTE })

  const store = useQualityApprovalSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )
  const { executeSearch: storeExecuteSearch, loadMore: storeLoadMore } = store

  const executeSearch = useCallback(
    (client: Client, params: QualityApprovalSearchFilters, force?: boolean) => {
      storeExecuteSearch(client, { ...params, taskViewScope }, force)
    },
    [storeExecuteSearch, taskViewScope]
  )

  const loadMore = useCallback(
    (client: Client, params: QualityApprovalSearchFilters) => {
      storeLoadMore(client, { ...params, taskViewScope })
    },
    [storeLoadMore, taskViewScope]
  )

  const {
    data: qualityApprovals,
    isFetching,
    error,
    sorting,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
  } = useListPageBase<QualityApproval, QualityApprovalSearchFilters>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
  })

  return {
    qualityApprovals,
    isFetching,
    error,
    sorting,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
  }
}
